import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { SuccessToast } from "app/pages/sales-intelligence/common-components/SuccessToast/SuccessToast";
import type { ScoringConfigRule } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

const TOAST_DURATION_MS = 3000;

type SaveRulesOptions = {
    onSuccess?: () => void;
    onError?: () => void;
    errorPrefixKey?: string;
    onErrorMessage?: (message: string) => void;
};

type SaveRulesFn = (newRules: ScoringConfigRule, isAddSignal: boolean, saveOptions?: SaveRulesOptions) => Promise<void>;

type PersistOptions = {
    errorPrefixKey?: string;
    onErrorMessage?: (message: string) => void;
};

/**
 * Shared scoring-board write plumbing: success toast and the persist wrapper around a
 * `useSaveRules` mutation. The mutation is passed per call so callers can route writes to the
 * correct config scope (single-config and merged multi-config boards both reuse this).
 * Failure messages are formatted in `useSaveRules`: callers can surface them inline in a modal
 * via `onErrorMessage`, or fall back to the default error toast for non-modal flows.
 */
export const useScoringBoardCrud = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const showSuccess = useCallback(
        (key: string) => {
            dispatch(showSuccessToast(<SuccessToast content={translate(key)} />, Date.now(), TOAST_DURATION_MS));
        },
        [dispatch, translate],
    );

    const persist = useCallback(
        (
            saveRules: SaveRulesFn,
            nextRules: ScoringConfigRule,
            successKey: string,
            onDone: () => void,
            options?: PersistOptions,
        ): void => {
            void saveRules(nextRules, false, {
                onSuccess: () => {
                    showSuccess(successKey);
                    onDone();
                },
                errorPrefixKey: options?.errorPrefixKey,
                onErrorMessage: options?.onErrorMessage,
            });
        },
        [showSuccess],
    );

    const persistOptimistic = useCallback(
        (
            saveRules: SaveRulesFn,
            nextRules: ScoringConfigRule,
            successKey: string,
            onDone: () => void,
            options?: PersistOptions,
        ): void => {
            void saveRules(nextRules, true, {
                onSuccess: () => {
                    showSuccess(successKey);
                    onDone();
                },
                errorPrefixKey: options?.errorPrefixKey,
                onErrorMessage: options?.onErrorMessage,
            });
        },
        [showSuccess],
    );

    return { showSuccess, persist, persistOptimistic };
};
